<template>
  <Container title="企业管理">
    <!-- 搜索卡片 -->
    <el-card shadow="never" class="search-card">
      <el-form :model="searchForm" label-position="left" label-width="70px" @submit.prevent @keyup.enter="handleSearch">
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12" :md="6" :lg="6">
            <el-form-item label="企业名称">
              <el-input v-model="searchForm.name" placeholder="请输入企业名称" clearable style="width: 100%;" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6" :lg="6">
            <el-form-item label="时间">
              <el-date-picker v-model="searchForm.dateRange" type="daterange" range-separator="至" start-placeholder="开始日期" 
			  end-placeholder="结束日期" value-format="YYYY-MM-DD" :shortcuts="shortcuts" style="width: 100%;" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6" :lg="6">
            <!-- 占位空白 -->
          </el-col>
          <el-col :span="6" style="display: flex; align-items: center; justify-content: flex-end;">
            <el-form-item label=" " label-width="0">
              <div style="display: flex; gap: 8px;">
                <el-button type="primary" @click="handleSearch">查询</el-button>
                <el-button @click="resetSearch">重置</el-button>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 表格卡片 -->
    <el-card shadow="never" class="table-card">
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <el-button v-if="isSuperAdmin && $hasPermission('btn:add_enterprise')" type="primary" @click="openAddDialog">
              <el-icon><Plus /></el-icon> 新建企业
            </el-button>
          </div>
          <div class="table-header-right">
            <el-tooltip content="刷新" placement="bottom"><el-button circle @click="fetchData"><el-icon><Refresh /></el-icon></el-button></el-tooltip>
            <el-tooltip content="导出" placement="bottom"><el-button circle @click="handleExport"><el-icon><Download /></el-icon></el-button></el-tooltip>
            <el-tooltip content="打印" placement="bottom"><el-button circle @click="handlePrint"><el-icon><Printer /></el-icon></el-button></el-tooltip>
          </div>
        </div>
      </template>

      <el-table :data="tableData" border style="width: 100%">
        <el-table-column prop="name" label="企业名" min-width="140" show-overflow-tooltip />
		<el-table-column label="提现结算类型" min-width="120" align="center">
		  <template #default="{ row }">
		    <el-tag :type="row.withdraw_type === 1 ? 'primary' : 'success'" size="small">
		      {{ row.withdraw_type === 1 ? '站点' : '企业' }}
		    </el-tag>
		  </template>
		</el-table-column>
        <el-table-column label="可提现金额" min-width="120" align="right">
          <template #default="{ row }">{{ Number(row.withdrawable_amount || 0).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column prop="province" label="省" min-width="90" show-overflow-tooltip />
        <el-table-column prop="city" label="市" min-width="90" show-overflow-tooltip />
        <el-table-column prop="district" label="区" min-width="90" show-overflow-tooltip />
        <el-table-column prop="detail_address" label="详细地址" min-width="160" show-overflow-tooltip />
        <el-table-column prop="contact" label="联系人" min-width="100" show-overflow-tooltip />
        <el-table-column prop="phone" label="手机号" min-width="110" />
        <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip />
        <el-table-column prop="created_at" label="创建时间" min-width="160">
          <template #default="{ row }">{{ formatTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" min-width="160" fixed="right" align="center">
          <template #default="{ row }">
            <el-button v-if="$hasPermission('btn:edit_enterprise')" type="warning" size="small" plain @click="openEditDialog(row)">修改</el-button>
            <el-popconfirm 
              v-if="isSuperAdmin && $hasPermission('btn:delete_enterprise')" 
              confirm-button-text="删除" 
              cancel-button-text="取消" 
              title="确定删除该企业吗？" 
              @confirm="handleDelete(row)"
            >
              <template #reference>
                <el-button type="danger" size="small" plain>删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="page.current"
          v-model:page-size="page.pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchData"
          @current-change="fetchData"
        />
      </div>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px" @close="closeDialog" destroy-on-close>
      <el-form :model="form" :rules="rules" ref="formRef" label-width="140px" label-position="right">
        <el-form-item label="企业名称：" prop="name">
          <el-input v-model="form.name" placeholder="请输入企业名称" style="width: 80%;" />
        </el-form-item>

        <el-form-item label="所在城市：" prop="region">
          <el-cascader
            v-model="form.region"
            :options="areaOptions"
            :props="cascaderProps"
            placeholder="请选择省/市/区"
            clearable
            style="width: 80%;"
            @change="handleRegionChange"
          />
        </el-form-item>

        <el-form-item label="详细地址：" prop="detail_address">
          <el-input v-model="form.detail_address" placeholder="请输入详细地址" style="width: 80%;" />
        </el-form-item>

        <el-form-item label="联系人：" prop="contact">
          <el-input v-model="form.contact" placeholder="请输入联系人" style="width: 80%;" />
        </el-form-item>

        <el-form-item label="电话：" prop="phone">
          <el-input v-model="form.phone" placeholder="请输入联系人电话" style="width: 80%;" />
        </el-form-item>

        <el-form-item label="提现结算：" prop="withdraw_type">
          <el-radio-group v-model="form.withdraw_type">
            <el-radio :value="1" border>站点</el-radio>
            <el-radio :value="2" border>企业</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="企业结算渠道：" prop="settlement_channel">
          <el-radio-group v-model="form.settlement_channel">
            <el-radio value="huifu" border>银盛</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="备注：" prop="remark">
          <el-input v-model="form.remark" type="textarea" :rows="3" placeholder="请输入备注" style="width: 80%;" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="closeDialog">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">保存</el-button>
      </template>
    </el-dialog>
	<!-- 导出弹窗 -->
	<ExportDialog
	  v-model:visible="exportDialogVisible"
	  :fields="exportFields"
	  :export-api="exportApi"
	  :get-search-params="getSearchParams"
	  :current-page="page"
	  :default-file-name="defaultFileName()"
	/>
  </Container>
</template>

<script>
const pca = require('china-division/dist/pca.json');

import { pub } from '/common/event';
import moment from 'moment';
import { Plus, Refresh, Download, Printer } from '@element-plus/icons-vue';
import { dateShortcuts } from '../../../utils/shortcuts.js';
import ExportDialog from '@/components/container/ExportDialog.vue';
import exportMixin from '@/mixins/exportMixin';

export default {
  components: { Plus, Refresh, Download, Printer,ExportDialog },
  mixins: [exportMixin],
  data() {
    return {
	  shortcuts: dateShortcuts, // 引用公共配置
      searchForm: { name: '', dateRange: null },
      tableData: [],
      total: 0,
      page: { current: 1, pageSize: 15 },
      dialogVisible: false,
      dialogTitle: '新建企业',
      editId: null,
      form: {
        name: '', region: [], province: '', city: '', district: '',
        detail_address: '', contact: '', phone: '',
        withdraw_type: 1, settlement_channel: 'huifu', remark: '', status: 1,
      },
      rules: {
        name: [{ required: true, message: '请输入企业名称', trigger: 'blur' }],
        phone: [{ pattern: /^1\d{10}$/, message: '请输入正确的手机号', trigger: 'blur' }],
      },
      areaOptions: this.buildAreaOptions(pca),
      cascaderProps: {
        label: 'name',
        value: 'name',
        children: 'children',
      },
      submitting: false,
	  exportFields: [
	    { key: 'id', label: '序号列', checked: true },
	    { key: 'name', label: '企业名', checked: true },
	    { key: 'withdraw_type_label', label: '提现结算类型', checked: true },
	    { key: 'withdrawable_amount', label: '可提现金额', checked: true },
	    { key: 'province', label: '省', checked: true },
	    { key: 'city', label: '市', checked: true },
	    { key: 'district', label: '区', checked: true },
	    { key: 'detail_address', label: '详细地址', checked: true },
	    { key: 'contact', label: '联系人', checked: true },
	    { key: 'phone', label: '手机号', checked: true },
	    { key: 'remark', label: '备注', checked: true },
	    { key: 'created_at_label', label: '创建时间', checked: true },
	  ],
    };
  },
  mounted() {
    this.fetchData();
  },
  computed: {
    exportApi() {
      return this.$api.enterprise.export;
    },
	isSuperAdmin() {
	  const u = this.$store.state.user.user || {};
	  return u.role === 'super_admin' || u.role_id === 1;
	},
  },
  methods: {
	defaultFileName() {
	  return '企业数据'; 
	},
    buildAreaOptions(data) {
      if (!data || typeof data !== 'object') return [];
      const provinceList = [];
      for (const [provinceName, cities] of Object.entries(data)) {
        if (typeof cities === 'object' && cities !== null) {
          const cityList = [];
          for (const [cityName, districts] of Object.entries(cities)) {
            const children = Array.isArray(districts)
              ? districts.map(district => ({ name: district }))
              : [];
            cityList.push({
              name: cityName,
              children: children.length > 0 ? children : undefined,
            });
          }
          provinceList.push({
            name: provinceName,
            children: cityList.length > 0 ? cityList : undefined,
          });
        }
      }
      return provinceList;
    },

    formatTime(ts) {
      if (!ts) return '';
      if (ts.toString().length === 10) ts = ts * 1000;
      return moment(ts).format('YYYY-MM-DD HH:mm:ss');
    },

    handleRegionChange(value) {
      if (value && value.length > 0) {
        this.form.province = value[0] || '';
        this.form.city = value[1] || '';
        this.form.district = value[2] || '';
      } else {
        this.form.province = '';
        this.form.city = '';
        this.form.district = '';
      }
    },

    async fetchData() {
      try {
        const params = { page: this.page.current, pageSize: this.page.pageSize, name: this.searchForm.name || undefined };
        if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
          params.startTime = new Date(this.searchForm.dateRange[0]).getTime();
          params.endTime = new Date(this.searchForm.dateRange[1]).getTime() + 24 * 60 * 60 * 1000 - 1;
        }
        const ret = await this.$api.enterprise.page(params);
        if (ret && ret.success) {
          this.tableData = ret.data.data || [];
          this.total = ret.data.total || 0;
          if (ret.data.page) {
            this.page.current = ret.data.page.current;
            this.page.pageSize = ret.data.page.pageSize;
          }
        } else { pub('error_message', ret?.message || '加载数据失败'); }
      } catch (e) { console.error('加载企业列表失败:', e); pub('error_message', '请求失败'); }
    },

    handleSearch() { this.page.current = 1; this.fetchData(); },

    resetSearch() { this.searchForm = { name: '', dateRange: null }; this.page.current = 1; this.fetchData(); },

    openAddDialog() {
      this.dialogTitle = '新建企业';
      this.editId = null;
      this.resetForm();
      this.dialogVisible = true;
      this.$nextTick(() => { this.$refs.formRef?.clearValidate(); });
    },

    openEditDialog(row) {
      this.dialogTitle = '编辑企业';
      this.editId = row.id;
      this.form = {
        name: row.name || '', region: [],
        province: row.province || '', city: row.city || '', district: row.district || '',
        detail_address: row.detail_address || '', contact: row.contact || '', phone: row.phone || '',
        withdraw_type: row.withdraw_type || 1, settlement_channel: row.settlement_channel || 'huifu',
        remark: row.remark || '', status: row.status || 1,
      };
      if (row.province) this.form.region.push(row.province);
      if (row.city) this.form.region.push(row.city);
      if (row.district) this.form.region.push(row.district);
      this.dialogVisible = true;
      this.$nextTick(() => { this.$refs.formRef?.clearValidate(); });
    },

    closeDialog() { this.dialogVisible = false; this.editId = null; this.resetForm(); },

    resetForm() {
      this.form = {
        name: '', region: [], province: '', city: '', district: '',
        detail_address: '', contact: '', phone: '',
        withdraw_type: 1, settlement_channel: 'huifu', remark: '', status: 1,
      };
    },

    async submitForm() {
      try { await this.$refs.formRef.validate(); } catch (e) { return; }
      this.submitting = true;
      try {
        const data = { ...this.form };
        delete data.region;
        let ret;
        if (this.editId) { data.id = this.editId; ret = await this.$api.enterprise.update(data); }
        else { ret = await this.$api.enterprise.create(data); }
        if (ret && ret.success) {
          pub('success_message', this.editId ? '编辑成功' : '添加成功');
          this.closeDialog();
          this.fetchData();
        } else { pub('error_message', ret?.message || '操作失败'); }
      } catch (e) { console.error('提交失败:', e); pub('error_message', '请求失败'); }
      finally { this.submitting = false; }
    },

    handleDelete(row) {
      this.$confirm('确定要删除该企业吗？', '提示', { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' })
        .then(async () => {
          try { const ret = await this.$api.enterprise.delete(row.id); if (ret && ret.success) { pub('success_message', '删除成功'); this.fetchData(); } else { pub('error_message', ret?.message || '删除失败'); } }
          catch (e) { pub('error_message', '请求失败'); }
        }).catch(() => {});
    },

    handleExport() { this.openExportDialog(); },
	getSearchParams() {
	  const params = { ...this.searchForm };
	  if (params.dateRange && params.dateRange.length === 2) {
	    params.startTime = new Date(params.dateRange[0]).getTime();
	    params.endTime = new Date(params.dateRange[1]).getTime() + 24 * 60 * 60 * 1000 - 1;
	    delete params.dateRange;
	  }
	  Object.keys(params).forEach(key => {
	    if (params[key] === null || params[key] === undefined || params[key] === '') {
	      delete params[key];
	    }
	  });
	  return params;
	},
    handlePrint() { window.print(); },
  },
};
</script>

<style scoped>
.search-card { margin-bottom: 16px; }
.search-card .el-form-item { margin-bottom: 0; }
.table-card :deep(.el-card__header) { padding: 10px 16px; border-bottom: 1px solid #e4e7ed; }
.table-header { display: flex; justify-content: space-between; align-items: center; width: 100%; }
.table-header-left { display: flex; align-items: center; gap: 8px; }
.table-header-right { display: flex; align-items: center; gap: 4px; }
.pagination-wrapper { display: flex; justify-content: center; margin-top: 16px; }
:deep(.el-radio.is-bordered) { padding: 6px 16px; margin-right: 12px; border-radius: 4px; border: 1px solid #dcdfe6; transition: border-color 0.2s; }
:deep(.el-radio.is-bordered.is-checked) { border-color: #409EFF; box-shadow: 0 0 0 1px #409EFF; }
</style>